import type { Client } from '@data-access/connect/tc-issue-management';
import { QueryClient } from '@tanstack/solid-query';

// Export types based on the topics service
export type ExportFormat = 'PDF' | 'BCF2_1' | 'BCF3_0';

export interface IExportSettings {
  viewpoints: boolean;
  comments: boolean;
  refdocs: boolean;
  originalImages: boolean;
}

export interface IExportJobDetail {
  projectId: string;
  jobId: string;
  type: 'EXPORT';
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  name: string;
  settings: IExportSettings;
  filter: Record<string, unknown>;
  package: IExportPackage;
  status: string;
  progress: IExportProgress;
  error?: IExportError;
  results?: IExportResults;
}

export interface IExportPackage {
  url?: string;
  archived: boolean;
  size?: number;
  format: ExportFormat;
}

export interface IExportProgress {
  percentage: number;
  message: string;
}

export interface IExportError {
  code: string;
  message: string;
}

export interface IExportResults {
  linksCount: number;
  links?: string[];
  linksUrl: string;
  log?: IExportLogEntity[];
}

export interface IExportLogEntity {
  level: string;
  code: string;
  message: string;
  link: string;
  location: string;
}

export interface ICreateExportJobPayload extends Record<string, unknown> {
  name: string;
  type: 'EXPORT';
  settings: IExportSettings;
  package: {
    format: ExportFormat;
    archived: boolean;
  };
  filter: Record<string, unknown>;
}

/**
 * Service for handling issue export operations
 * Uses the same API endpoints as the topics service for BCF exchange
 */
export class IssueExportApiService {
  private readonly bcfExchangeUrl = 'bcf-exchange';

  constructor(
    private readonly client: Client,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore TS6138: Reserved for future use
    private readonly queryClient: QueryClient
  ) {}

  /**
   * Get the base URL for BCF exchange API
   */
  private getBcfExchangeBaseUrl(): string {
    // Since we're using a dedicated BCF client with the base URL already configured,
    // we only need the relative path
    return this.bcfExchangeUrl;
  }

  /**
   * Create a new export job
   */
  public async createExportJob(
    projectId: string,
    payload: ICreateExportJobPayload
  ): Promise<IExportJobDetail> {
    try {
      const url = `${this.getBcfExchangeBaseUrl()}/projects/${projectId}/jobs`;

      const response = await this.client.request({
        method: 'POST',
        url,
        body: payload,
        headers: {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'Content-Type': 'application/json',
        },
      });

      if (response.error) {
        throw new Error(`API Error: ${JSON.stringify(response.error)}`);
      }

      return response.data as IExportJobDetail;
    } catch (error) {
      console.error('Failed to create export job:', error);
      throw new Error(
        `Failed to create export job: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      );
    }
  }

  /**
   * Get export job details by job ID
   */
  public async getExportJobDetail(
    projectId: string,
    jobId: string
  ): Promise<IExportJobDetail> {
    try {
      const url = `${this.getBcfExchangeBaseUrl()}/projects/${projectId}/jobs/${jobId}?fields=results.log,results.links`;

      const response = await this.client.request({
        method: 'GET',
        url,
      });

      if (response.error) {
        throw new Error(`API Error: ${JSON.stringify(response.error)}`);
      }

      return response.data as IExportJobDetail;
    } catch (error) {
      console.error('Failed to get export job detail:', error);
      throw new Error(
        `Failed to get export job detail: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      );
    }
  }

  /**
   * List export jobs for a project
   */
  public async listExportJobs(projectId: string): Promise<IExportJobDetail[]> {
    try {
      const url = `${this.getBcfExchangeBaseUrl()}/projects/${projectId}/jobs?type=EXPORT&fields=results.log,results.links`;

      const response = await this.client.request({
        method: 'GET',
        url,
      });

      if (response.error) {
        throw new Error(`API Error: ${JSON.stringify(response.error)}`);
      }

      const data = response.data as { items: IExportJobDetail[] };
      return data.items;
    } catch (error) {
      console.error('Failed to list export jobs:', error);
      throw new Error(
        `Failed to list export jobs: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      );
    }
  }
}
